import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Badge,
  Button,
  Spinner,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { APIClient } from "../../helpers/api_helper";
import { toast } from "react-toastify";

const api = new APIClient();

/* ── Types ─────────────────────────────────────── */
interface Bill {
  id: number;
  month: number;
  year: number;
  amount: number;
  penalty: number;
  discount: number;
  totalAmount: number;
  dueDate: string;
  status: "PAID" | "UNPAID" | "OVERDUE";
  createdAt: string;
  payment?: {
    id: number;
    paymentDate: string;
    paymentMode: string;
    transactionId?: string;
    receipt?: {
      id: number;
      receiptNo: string;
    };
  };
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ResidentBills: React.FC = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<"pending" | "advance" | "paid">("pending");
  const [selectedBills, setSelectedBills] = useState<number[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [paying, setPaying] = useState(false);

  // ✅ Add separate counts
  const [unitsCount, setUnitsCount] = useState(0);
  const [selfUnitsCount, setSelfUnitsCount] = useState(0);
  const [tenantUnitsCount, setTenantUnitsCount] = useState(0);
  const [unitsList, setUnitsList] = useState<any[]>([]);

  const previousBillCountRef = useRef<number>(0);
  const isFirstLoadRef = useRef<boolean>(true);

  // ✅ Fetch Profile - Get self vs tenant units
  const fetchProfile = async () => {
    try {
      const res: any = await api.get("/api/resident/profile");
      
      if (res?.success && res?.data) {
        const units = res.data.unit || res.data.units || [];
        
        // ✅ Filter self vs tenant units
        const selfUnits = units.filter((u: any) => !u.tenantId);
        const tenantUnits = units.filter((u: any) => u.tenantId);
        
        setUnitsCount(units.length);
        setSelfUnitsCount(selfUnits.length);
        setTenantUnitsCount(tenantUnits.length);
        setUnitsList(units);
        
        console.log("✅ Profile loaded:");
        console.log("   Total units:", units.length);
        console.log("   Self units:", selfUnits.length);
        console.log("   Tenant units:", tenantUnits.length);
      }
    } catch (err) {
      console.error("Profile error:", err);
    }
  };

  // ✅ Fetch on mount
  useEffect(() => {
    fetchProfile();
    fetchBills();
    const interval = setInterval(() => fetchBills(true), 30000);
    return () => clearInterval(interval);
  }, []);

  /* ── Dynamic Discount Plans - USE SELF UNITS ONLY ── */
  const DISCOUNT_PLANS = useMemo(() => {
    // ✅ Use selfUnitsCount instead of unitsCount
    const perMonthCombined = 500 * selfUnitsCount;
    
    if (selfUnitsCount === 0) return [];
    
    return [
      {
        months: 3,
        discount: 10,
        baseAmount: perMonthCombined * 3,
        finalAmount: Math.round((perMonthCombined * 3) * 0.9),
        saved: Math.round((perMonthCombined * 3) * 0.1),
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.1)",
        popular: false,
      },
      {
        months: 6,
        discount: 15,
        baseAmount: perMonthCombined * 6,
        finalAmount: Math.round((perMonthCombined * 6) * 0.85),
        saved: Math.round((perMonthCombined * 6) * 0.15),
        color: "#8B5CF6",
        bg: "rgba(139, 92, 246, 0.1)",
        popular: true,
      },
      {
        months: 12,
        discount: 20,
        baseAmount: perMonthCombined * 12,
        finalAmount: Math.round((perMonthCombined * 12) * 0.80),
        saved: Math.round((perMonthCombined * 12) * 0.20),
        color: "#16A34A",
        bg: "rgba(22, 163, 74, 0.1)",
        popular: false,
      },
    ];
  }, [selfUnitsCount]); // ← Depends on selfUnitsCount

  /* ── Fetch Bills ── */
  const fetchBills = async (isAutoRefresh = false) => {
    try {
      if (!isAutoRefresh) setLoading(true);
      else setRefreshing(true);

      const res: any = await api.get("/api/resident/bills");
      const newBills: Bill[] = Array.isArray(res?.data) ? res.data : [];

      if (!isFirstLoadRef.current && newBills.length > previousBillCountRef.current) {
        const newCount = newBills.length - previousBillCountRef.current;
        toast.info(`🎉 ${newCount} new bill${newCount > 1 ? "s" : ""} received!`, { autoClose: 5000 });
      }

      if (!isFirstLoadRef.current) {
        const oldOverdueCount = bills.filter((b) => b.status === "OVERDUE").length;
        const newOverdueCount = newBills.filter((b) => b.status === "OVERDUE").length;

        if (newOverdueCount > oldOverdueCount) {
          toast.warning(`⚠️ Penalty applied on ${newOverdueCount - oldOverdueCount} bill(s)!`, { autoClose: 5000 });
        }
      }

      setBills(newBills);
      previousBillCountRef.current = newBills.length;
      isFirstLoadRef.current = false;
    } catch (err) {
      if (!isAutoRefresh) toast.error("Failed to load bills");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /* ── Manual Refresh ── */
  const handleManualRefresh = () => {
    fetchBills(true);
    toast.success("Bills refreshed!", { autoClose: 1500 });
  };

  /* ── Categorize Bills ── */
  const pendingBills = useMemo(() => bills.filter((b) => b.status !== "PAID"), [bills]);
  const paidBills = useMemo(() => bills.filter((b) => b.status === "PAID"), [bills]);

  /* ── Stats ── */
  const stats = useMemo(() => ({
    pendingCount: pendingBills.length,
    pendingAmount: pendingBills.reduce((sum, b) => sum + b.totalAmount, 0),
    paidCount: paidBills.length,
    paidAmount: paidBills.reduce((sum, b) => sum + b.totalAmount, 0),
    overdueCount: pendingBills.filter((b) => b.status === "OVERDUE").length,
    totalPenalty: pendingBills.reduce((sum, b) => sum + b.penalty, 0),
  }), [pendingBills, paidBills]);

  /* ── Selected Amount ── */
  const selectedAmount = useMemo(() => {
    return pendingBills
      .filter((b) => selectedBills.includes(b.id))
      .reduce((sum, b) => sum + b.totalAmount, 0);
  }, [selectedBills, pendingBills]);

  /* ── Toggle Bill Selection ── */
  const toggleSelectBill = (id: number) => {
    setSelectedBills((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBills.length === pendingBills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(pendingBills.map((b) => b.id));
    }
  };

  /* ── Razorpay Payment ── */
  const payWithRazorpay = async (bill: Bill) => {
    try {
      setPaying(true);
      toast.info("Creating order...");

      const orderRes: any = await api.create(
        "/api/resident/payment/create-order",
        { billId: bill.id }
      );

      if (!orderRes?.success) {
        toast.error("Failed to create order");
        setPaying(false);
        return;
      }

      const options = {
        key: orderRes.key,
        amount: orderRes.order.amount,
        currency: orderRes.order.currency,
        order_id: orderRes.order.id,
        name: "Building Maintenance",
        description: `Bill: ${MONTH_NAMES[bill.month - 1]} ${bill.year}`,
        prefill: {
          name: orderRes.resident.name,
          email: orderRes.resident.email,
          contact: orderRes.resident.phone,
        },
        theme: { color: "#764ba2" },
        handler: async function (response: any) {
          try {
            toast.info("Verifying payment...");
            const verifyRes: any = await api.create(
              "/api/resident/payment/verify",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                billId: bill.id,
              }
            );

            if (verifyRes?.success) {
              toast.success(`🎉 Payment successful! Receipt: ${verifyRes.data.receiptNo}`, { autoClose: 5000 });
              await fetchBills();
            } else {
              toast.error("Payment verification failed!");
            }
          } catch (err: any) {
            toast.error("Verification error: " + err.message);
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast.info("Payment cancelled");
            setPaying(false);
          },
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.on("payment.failed", function (response: any) {
        toast.error("Payment failed: " + response.error.description);
        setPaying(false);
      });
      razorpay.open();
    } catch (error: any) {
      toast.error("Payment error: " + error.message);
      setPaying(false);
    }
  };

  const handlePaySingle = (bill: Bill) => {
    payWithRazorpay(bill);
  };

  const handlePaySelected = () => {
    if (selectedBills.length === 0) {
      toast.warning("Select bills to pay!");
      return;
    }
    const firstBill = pendingBills.find((b) => b.id === selectedBills[0]);
    if (firstBill) {
      toast.info(`Paying for ${MONTH_NAMES[firstBill.month - 1]} ${firstBill.year}...`);
      payWithRazorpay(firstBill);
    }
  };

  /* ── Pay Advance ── */
  const handlePayAdvance = async () => {
    if (selectedPlan === null) {
      toast.warning("Select an advance plan!");
      return;
    }

    const plan = DISCOUNT_PLANS[selectedPlan];

    try {
      setPaying(true);
      toast.info(`Creating order for ${plan.months} months...`);

      const orderRes: any = await api.create(
        "/api/resident/payment/create-advance-order",
        { months: plan.months }
      );

      if (!orderRes?.success) {
        toast.error("Failed to create order");
        setPaying(false);
        return;
      }

      const options = {
        key: orderRes.key,
        amount: orderRes.order.amount,
        currency: orderRes.order.currency,
        order_id: orderRes.order.id,
        name: "Building Maintenance",
        description: `Advance Payment: ${orderRes.order.planLabel} (Save ₹${orderRes.order.saved})`,
        prefill: {
          name: orderRes.resident.name,
          email: orderRes.resident.email,
          contact: orderRes.resident.phone,
        },
        theme: { color: plan.color },
        notes: {
          type: "advance_payment",
          months: plan.months.toString(),
          discount: plan.discount.toString(),
        },
        handler: async function (response: any) {
          try {
            toast.info("Verifying advance payment...");
            const verifyRes: any = await api.create(
              "/api/resident/payment/verify-advance",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                months: plan.months,
                finalAmount: plan.finalAmount,
              }
            );

            if (verifyRes?.success) {
              toast.success(
                `🎉 Advance payment successful! You saved ₹${verifyRes.data.discount}! Receipt: ${verifyRes.data.receiptNo}`,
                { autoClose: 8000 }
              );
              setSelectedPlan(null);
              await fetchBills();
            } else {
              toast.error("Verification failed!");
            }
          } catch (err: any) {
            toast.error("Error: " + err.message);
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: function () {
            toast.info("Payment cancelled");
            setPaying(false);
          },
        },
      };

      const razorpay = new (window as any).Razorpay(options);
      razorpay.on("payment.failed", function (response: any) {
        toast.error("Payment failed: " + response.error.description);
        setPaying(false);
      });
      razorpay.open();
    } catch (error: any) {
      toast.error("Payment error: " + error.message);
      setPaying(false);
    }
  };

  /* ── Download Receipt ── */
  const downloadReceipt = async (bill: Bill) => {
    if (!bill.payment?.receipt) {
      toast.error("Receipt not available");
      return;
    }

    try {
      toast.info("Generating receipt...");
      const token = localStorage.getItem("residentToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/receipts/download/${bill.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Receipt_${bill.payment.receipt.receiptNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Receipt downloaded! 📄");
    } catch {
      toast.error("Failed to download receipt");
    }
  };

  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-2">Loading bills...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">
            <i className="ri-bill-line me-2 text-primary" />
            My Bills & Payments
          </h4>
          <small className="text-muted">
            <i className="ri-refresh-line me-1" />
            Auto-refreshes every 30 seconds
          </small>
        </div>
        <Button color="primary" outline size="sm" onClick={handleManualRefresh} disabled={refreshing}>
          {refreshing ? (
            <><Spinner size="sm" className="me-1" />Refreshing...</>
          ) : (
            <><i className="ri-refresh-line me-1" />Refresh Now</>
          )}
        </Button>
      </div>

      {/* Stats Cards - keep same */}
      <Row className="g-3 mb-4">
        {[
          {
            label: "Pending Bills",
            value: stats.pendingCount,
            amount: `₹${stats.pendingAmount.toLocaleString("en-IN")}`,
            icon: "ri-time-line",
            color: "#F59E0B",
            bg: "linear-gradient(135deg, #fef3c7, #fde68a)",
          },
          {
            label: "Paid Bills",
            value: stats.paidCount,
            amount: `₹${stats.paidAmount.toLocaleString("en-IN")}`,
            icon: "ri-check-double-line",
            color: "#16A34A",
            bg: "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          },
          {
            label: "Overdue",
            value: stats.overdueCount,
            amount: stats.overdueCount > 0 ? "Pay urgently!" : "All clear!",
            icon: "ri-error-warning-line",
            color: stats.overdueCount > 0 ? "#DC2626" : "#16A34A",
            bg: stats.overdueCount > 0 ? "linear-gradient(135deg, #fee2e2, #fecaca)" : "linear-gradient(135deg, #dcfce7, #bbf7d0)",
          },
          {
            label: "Total Penalty",
            value: `₹${stats.totalPenalty}`,
            amount: stats.totalPenalty > 0 ? "Avoid more!" : "None",
            icon: "ri-money-rupee-circle-line",
            color: stats.totalPenalty > 0 ? "#DC2626" : "#16A34A",
            bg: "linear-gradient(135deg, #fef2f2, #fee2e2)",
          },
        ].map((s, i) => (
          <Col key={i} xs={6} md={3}>
            <Card className="stat-card border-0 h-100" style={{ borderRadius: "12px", "--stat-color": s.color } as React.CSSProperties}>
              <CardBody style={{ background: s.bg, borderRadius: "12px" }}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{
                    width: "48px", height: "48px", borderRadius: "12px",
                    background: "#fff", display: "flex", alignItems: "center",
                    justifyContent: "center", color: s.color, fontSize: "24px",
                  }}>
                    <i className={s.icon} />
                  </div>
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: 700, color: s.color, lineHeight: 1.2 }}>
                      {s.value}
                    </div>
                    <div style={{ fontSize: "11px", color: "#475569" }}>{s.label}</div>
                    <div style={{ fontSize: "10px", color: "#64748b", marginTop: 2 }}>{s.amount}</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Tabs */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", background: "var(--bg-card)" }}>
        <CardBody>
          <Nav tabs className="mb-4" style={{ borderBottom: "2px solid var(--border-color)" }}>
            <NavItem>
              <NavLink
                onClick={() => setActiveTab("pending")}
                style={{
                  cursor: "pointer", fontWeight: 600, fontSize: 14,
                  padding: "10px 20px", border: "none",
                  borderBottom: activeTab === "pending" ? "3px solid #764ba2" : "3px solid transparent",
                  color: activeTab === "pending" ? "#764ba2" : "var(--text-secondary)",
                }}
              >
                <i className="ri-time-line me-1" />
                Pending Bills ({pendingBills.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                onClick={() => setActiveTab("advance")}
                style={{
                  cursor: "pointer", fontWeight: 600, fontSize: 14,
                  padding: "10px 20px", border: "none",
                  borderBottom: activeTab === "advance" ? "3px solid #764ba2" : "3px solid transparent",
                  color: activeTab === "advance" ? "#764ba2" : "var(--text-secondary)",
                }}
              >
                <i className="ri-gift-line me-1" />
                Advance Pay
                <Badge color="warning" pill className="ms-1" style={{ fontSize: 10 }}>
                  Save up to 20%
                </Badge>
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* PENDING BILLS TAB - keep same */}
            <TabPane tabId="pending">
              {/* ... existing pending bills code ... */}
              {pendingBills.length > 0 ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3 p-3" style={{ background: "var(--hover-bg)", borderRadius: "10px" }}>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="checkbox"
                        checked={selectedBills.length === pendingBills.length && pendingBills.length > 0}
                        onChange={toggleSelectAll}
                      />
                      <span style={{ fontSize: 14 }}>
                        <strong>{selectedBills.length}</strong> of <strong>{pendingBills.length}</strong> selected
                      </span>
                    </div>
                    {selectedBills.length > 0 && (
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: 14 }}>
                          Total: <strong style={{ fontSize: 18, color: "#185FA5" }}>
                            ₹{selectedAmount.toLocaleString("en-IN")}
                          </strong>
                        </span>
                        <Button color="success" onClick={handlePaySelected} size="sm" disabled={paying} style={{ fontWeight: 600 }}>
                          <i className="ri-wallet-line me-1" />Pay First Selected
                        </Button>
                      </div>
                    )}
                  </div>

                  {pendingBills.map((bill) => {
                    const daysLeft = getDaysUntilDue(bill.dueDate);
                    const isOverdue = bill.status === "OVERDUE";
                    const isSelected = selectedBills.includes(bill.id);
                    return (
                      <div key={bill.id} style={{
                        padding: "16px",
                        border: `2px solid ${isSelected ? "#764ba2" : isOverdue ? "#fecaca" : "var(--border-color)"}`,
                        borderRadius: "12px",
                        background: isSelected ? "rgba(118, 75, 162, 0.1)" : isOverdue ? "rgba(220, 38, 38, 0.1)" : "var(--bg-item)",
                        marginBottom: "10px",
                      }}>
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-3">
                          <div className="d-flex align-items-start gap-3 flex-grow-1">
                            <div className="pt-1">
                              <Input type="checkbox" checked={isSelected} onChange={() => toggleSelectBill(bill.id)} style={{ width: 18, height: 18, cursor: "pointer" }} />
                            </div>
                            <div>
                              <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                <Badge color={isOverdue ? "danger" : "warning"} pill style={{ fontSize: 10 }}>
                                  {bill.status}
                                </Badge>
                                <span className="fw-bold" style={{ fontSize: 16 }}>
                                  {MONTH_NAMES[bill.month - 1]} {bill.year}
                                </span>
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                <i className="ri-calendar-line me-1" />
                                Due: {new Date(bill.dueDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                {!isOverdue && daysLeft > 0 && <span className="ms-2 text-warning fw-medium">• {daysLeft} days left</span>}
                                {isOverdue && <span className="ms-2 text-danger fw-medium">• {Math.abs(daysLeft)} days overdue!</span>}
                              </div>
                              {bill.penalty > 0 && (
                                <div className="text-danger mt-1" style={{ fontSize: 11 }}>
                                  <i className="ri-error-warning-line me-1" />
                                  Base: ₹{bill.amount} + Penalty: ₹{bill.penalty} (20%)
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex flex-row flex-sm-column align-items-center align-items-sm-end justify-content-between ms-0 ms-sm-3 pt-2 pt-sm-0" style={{ borderTop: "1px dashed var(--border-color)", borderTopWidth: window.innerWidth < 576 ? 1 : 0 }}>
                            <div className="fw-bold mb-0 mb-sm-2" style={{ fontSize: 24, color: isOverdue ? "#dc2626" : "#185FA5" }}>
                              ₹{bill.totalAmount.toLocaleString("en-IN")}
                            </div>
                            <Button color="primary" onClick={() => handlePaySingle(bill)} disabled={paying} style={{
                              fontWeight: 600, fontSize: 13,
                              background: "linear-gradient(135deg, #764ba2, #764ba2)",
                              border: "none", padding: "6px 20px"
                            }}>
                              {paying ? (<><Spinner size="sm" className="me-1" />Processing...</>) : (<><i className="ri-wallet-line me-1" />Pay Now</>)}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: 80, marginBottom: 8 }}>🎉</div>
                  <h5 className="fw-bold text-success">All Paid Up!</h5>
                  <p className="text-muted">You have no pending bills.</p>
                </div>
              )}
            </TabPane>

            {/* ✅ ADVANCE PAY TAB - UPDATED */}
            <TabPane tabId="advance">
              <div className="text-center mb-4">
                <h5 className="fw-bold text-primary">💎 Save More with Advance Payment</h5>
                <p className="text-muted">Pay for multiple months upfront and save big!</p>
                
                {/* ✅ NEW: Info Card about Self vs Tenant Units */}
                <div style={{
                  background: tenantUnitsCount > 0 ? "rgba(245, 158, 11, 0.1)" : "rgba(59, 130, 246, 0.1)",
                  padding: "12px 20px",
                  borderRadius: "10px",
                  display: "inline-block",
                  marginTop: "10px",
                  border: `1px solid ${tenantUnitsCount > 0 ? "#F59E0B" : "#3B82F6"}`,
                  maxWidth: "600px"
                }}>
                  <div style={{ fontSize: 13 }}>
                    <i className="ri-information-line me-1" style={{ color: tenantUnitsCount > 0 ? "#92400E" : "#1E40AF" }} />
                    <strong>Note:</strong> Advance covers only your <strong style={{ color: "#16A34A" }}>{selfUnitsCount} self-occupied units</strong>
                    {tenantUnitsCount > 0 && (
                      <div className="mt-1" style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                        Your <strong>{tenantUnitsCount} tenant unit{tenantUnitsCount > 1 ? "s" : ""}</strong> will be billed to tenants separately
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ✅ Show plans only if self units exist */}
              {selfUnitsCount === 0 ? (
                <div className="text-center py-5">
                  <div style={{ fontSize: 60 }}>🏢</div>
                  <h5 className="text-muted">No Self Units</h5>
                  <p className="text-muted">
                    All your units are rented to tenants. They will pay their own bills.
                  </p>
                </div>
              ) : (
                <>
                  <Row className="g-3">
                    {DISCOUNT_PLANS.map((plan, i) => (
                      <Col key={i} xs={12} md={4}>
                        <div onClick={() => setSelectedPlan(i)} style={{
                          padding: "24px",
                          border: `3px solid ${selectedPlan === i ? plan.color : "var(--border-color)"}`,
                          borderRadius: "16px",
                          background: selectedPlan === i ? plan.bg : "var(--bg-item)",
                          cursor: "pointer",
                          position: "relative",
                          height: "100%",
                        }}>
                          {plan.popular && (
                            <Badge color="warning" pill style={{
                              position: "absolute", top: -10, right: 16,
                              fontSize: 11, padding: "4px 10px",
                            }}>
                              🔥 POPULAR
                            </Badge>
                          )}
                          <div className="text-center">
                            <div style={{
                              width: 60, height: 60, borderRadius: "50%",
                              background: plan.color, color: "#fff",
                              display: "inline-flex", alignItems: "center",
                              justifyContent: "center", marginBottom: 12,
                              fontSize: 24, fontWeight: 700,
                            }}>
                              {plan.months}
                            </div>
                            <h6 className="fw-bold mb-1">{plan.months} Months</h6>
                            <Badge color="success" pill style={{ fontSize: 14, padding: "5px 12px" }}>
                              {plan.discount}% OFF
                            </Badge>
                            
                            {/* ✅ Show self units info */}
                            <div className="mt-2 text-muted" style={{ fontSize: 10 }}>
                              For {selfUnitsCount} self unit{selfUnitsCount > 1 ? "s" : ""}
                            </div>
                            
                            <div className="mt-3">
                              <div className="text-muted" style={{ fontSize: 11 }}>Original Price</div>
                              <div className="text-muted" style={{ textDecoration: "line-through", fontSize: 14 }}>
                                ₹{plan.baseAmount}
                              </div>
                            </div>
                            <div className="my-2">
                              <div className="fw-bold" style={{ fontSize: 32, color: plan.color }}>
                                ₹{plan.finalAmount}
                              </div>
                            </div>
                            <div style={{ background: "rgba(22, 163, 74, 0.15)", padding: "8px", borderRadius: "8px", marginTop: 12 }}>
                              <span className="text-success fw-bold" style={{ fontSize: 13 }}>
                                💰 Save ₹{plan.saved}
                              </span>
                            </div>
                            {selectedPlan === i && (
                              <div className="mt-3">
                                <i className="ri-check-double-line text-success" style={{ fontSize: 24 }} />
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>

                  {selectedPlan !== null && (
                    <div className="text-center mt-4">
                      <Button color="primary" size="lg" onClick={handlePayAdvance} disabled={paying} style={{
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #764ba2, #764ba2)",
                        border: "none",
                        padding: "12px 40px",
                      }}>
                        {paying ? (
                          <><Spinner size="sm" className="me-2" />Processing...</>
                        ) : (
                          <><i className="ri-wallet-line me-2" />Pay ₹{DISCOUNT_PLANS[selectedPlan].finalAmount} (Save ₹{DISCOUNT_PLANS[selectedPlan].saved})</>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default ResidentBills;